import {
    Button, ButtonGroup, Count, DialogBody, DialogContainer, DialogFooter, DialogHeader, Interpolate
} from '@wg/wows-react-uikit';
import classnames from 'classnames';
import get from 'lodash/get';
import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import styles from './MultipleBuyDialog.scss';

import type { RootState } from '~/store';
import type { i18n_Props } from '~/i18n';
import type { Action } from 'redux';
import type { IBooster, IReward } from '~/types';
import type { ThunkDispatch } from 'redux-thunk';

import { DWH_EXPORT_EVENTS } from '~/constants';
import dwhExport from '~/dwhExport';

import { buyBoosters } from '~/store/buy';
import { setPredictProgress } from '~/store/app';
import { closeMultipleBuyDialog } from '~/store/dialogs';

import { Button as DockyardButton } from '~/Components/Buttons/Buttons';
import Currency from '~/Components/Currency/Currency';
import { NotEnouthMoney } from '~/Components/Messages/Messages';
import RangeRewards from '~/Components/RangeRewards/RangeRewards';
import RewardShip from '~/Components/RewardShip/RewardShip';
import Slider, { SliderLabels } from '~/Components/Slider/Slider';
import WidthWrapper from '~/Components/WidthWrapper/WidthWrapper';
import Wreath from '~/Components/Wreath/Wreath';
import { getWreathColor } from '~/utils/rewards';
import { playButtonClickSound } from '@wg/web2clientapi/sound';


type OwnProps = i18n_Props;

interface DispatchProps {
    closeMultipleBuyDialog: () => void,
    buyBoosters: (isPremium: boolean) => void,
    setPredictProgress: (predictProgress: number) => void,
}

interface StateProps {
    predictProgress: number,
    isMultipleBuyDialogVisible: boolean,
    boosters: Array<IBooster>,
    premiumBooster: IBooster,
    progress: number,
    blueprintsCount: number,
    gold: number,
    rewards: Array<IReward>,
    mainRewardShipId: string,
}

export type Props = StateProps & OwnProps & DispatchProps;

const COUNTER_ROW_ID = 'multiple-buy-counter-row';

class MultipleBuyDialog extends Component<Props> {
    componentDidMount() {
        document.addEventListener('keydown', this.onEnter, false);
        this.props.setPredictProgress(1);
    }

    shouldComponentUpdate(nextProps: Props) {
        return (
            this.props.predictProgress !== nextProps.predictProgress
            || this.props.isMultipleBuyDialogVisible !== nextProps.isMultipleBuyDialogVisible
            || this.props.boosters.length !== nextProps.boosters.length
            || (this.props.premiumBooster && this.props.premiumBooster.id)
                !== (nextProps.premiumBooster && nextProps.premiumBooster.id)
            || this.props.progress !== nextProps.progress
            || this.props.gold !== nextProps.gold
            || this.props.rewards.length !== nextProps.rewards.length
        );
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.onEnter, false);
    }

    onEnter = (e: KeyboardEvent) => {
        if (e.keyCode === 13) {
            e.preventDefault();
            this.props.buyBoosters(true);
        }
    };

    onCounterChange = (value: number) => {
        this.props.setPredictProgress(value);
    };

    onSliderChange = (value: number) => {
        dwhExport.send(DWH_EXPORT_EVENTS.SET_MULTIPLE_BOOSTER_BUY_AMOUNT, {
            amount: value,
        });
    };

    onChange = (value: number) => {
        this.onCounterChange(value);
        this.onSliderChange(value);
    };

    getPrice = () => {
        const boosterPrice = this.props.premiumBooster ? this.props.premiumBooster.price : 0;
        return boosterPrice * this.props.predictProgress;
    };

    getMaxValue = () => {
        const price = this.props.premiumBooster ? this.props.premiumBooster.price : 0;
        const left = this.props.rewards.length - this.props.progress;
        let max = Math.floor(this.props.gold / price);
        if (max > left) {
            max = left;
        }
        if (max === 0) {
            max++;
        }
        return max;
    };

    onSliderRewardClick = () => {
        playButtonClickSound();
        const max = this.getMaxValue();
        this.onCounterChange(max);
    };

    buyBoosters = () => {
        this.props.buyBoosters(true);
    };

    render() {
        const { t, gold } = this.props;
        const price = this.getPrice();
        const notEnoughMoney = gold < price;
        const needGold = price - gold;
        const sliderLabels: SliderLabels = [];
        const isMainRewardHighlighted = this.props.rewards.length === this.props.progress + this.props.predictProgress;

        const sliderReward = classnames(styles.sliderReward, {
            [styles.sliderRewardActive]: isMainRewardHighlighted,
        });

        this.props.rewards.forEach((reward) => {
            const hasWreath = reward.grade !== 'normal';
            if (hasWreath) {
                const wreathColor = getWreathColor(reward.grade);
                sliderLabels.push({
                    index: reward.shipLevelNumber,
                    content: (
                        <div className={styles.wreath}>
                            <Wreath color={wreathColor} />
                            <div
                                className={styles.wreathLabel}
                                style={{
                                    color: wreathColor,
                                }}
                            >
                                {reward.shipLevelNumber}
                            </div>
                        </div>
                    ),
                });
            }
        });

        return (
            <DialogContainer>
                <DialogHeader>{t('Завершение этапов')}</DialogHeader>
                <DialogBody>
                    <WidthWrapper>
                        <div className={styles.messageWrapper}>
                            <div className={styles.message}>
                                {t(
                                    'Вы можете завершить этапы строительства за дублоны, сразу получить за них награды и с помощью этого заполнить шкалу прогресса строительства.',
                                )}
                            </div>
                        </div>
                        <div className={styles.sliderWrapper}>
                            <Slider
                                min={0}
                                max={this.props.rewards.length}
                                minValue={1}
                                value={this.props.predictProgress}
                                progress={this.props.progress}
                                labels={sliderLabels}
                                onDrag={this.onCounterChange}
                                onChange={this.onSliderChange}
                                blueprintsCount={this.props.blueprintsCount}
                                after={
                                    <div className={sliderReward} onClick={this.onSliderRewardClick}>
                                        <RewardShip
                                            id={this.props.mainRewardShipId}
                                            fixedTooltip={true}
                                            raysVisible={isMainRewardHighlighted}
                                            reward={this.props.rewards[this.props.rewards.length - 1]}
                                        />
                                    </div>
                                }
                            />
                        </div>
                    </WidthWrapper>
                    <div className={styles.rewardsWrapper}>
                        <RangeRewards
                            from={this.props.progress}
                            to={this.props.progress + this.props.predictProgress}
                            filteredShips={[this.props.mainRewardShipId]}
                            isLastIncluded={
                                this.props.rewards.length === this.props.progress + this.props.predictProgress
                            }
                        />
                    </div>
                </DialogBody>
                <DialogFooter>
                    <div className={styles.counter} id={COUNTER_ROW_ID}>
                        <Interpolate
                            str={t('Завершить %(counter)s этап за %(price)s ?', {
                                count: this.props.predictProgress,
                            })}
                            counter={
                                <div className={styles.countWrapper}>
                                    <Count
                                        value={this.props.predictProgress}
                                        min={1}
                                        max={this.getMaxValue()}
                                        onChange={this.onChange}
                                    />
                                </div>
                            }
                            price={
                                <div className={styles.priceWrapper}>
                                    <Currency amount={price} />
                                </div>
                            }
                        />
                    </div>
                    <ButtonGroup>
                        <Button
                            isDisabled={notEnoughMoney}
                            type={'orange'}
                            isFlat={true}
                            tooltipContent={notEnoughMoney && <NotEnouthMoney needGold={needGold} />}
                            onClick={this.buyBoosters}
                        >
                            {t('Да')}
                        </Button>
                        <DockyardButton label={t('Нет')} onClick={this.props.closeMultipleBuyDialog} />
                    </ButtonGroup>
                </DialogFooter>
            </DialogContainer>
        );
    }
}

const mapStateToProps = (state: RootState): StateProps => {
    const boosters = state.app.boosters.filter((booster) => booster.isPremium);
    const gold = get(state.app, 'account.wallet.gold', 0) as number;
    const premiumBooster = state.app.boosters.filter((booster) => booster.isPremium && !booster.unique)[0];

    return {
        gold,
        premiumBooster,
        boosters,
        predictProgress: state.app.predictProgress,
        progress: state.app.account.progress || 0,
        blueprintsCount: state.app.blueprintsCount,
        isMultipleBuyDialogVisible: state.dialogs.isMultipleBuyDialogVisible,
        mainRewardShipId: state.app.mainRewardShipId,
        rewards: state.app.rewards,
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, unknown, Action>): DispatchProps => {
    return {
        buyBoosters: (isPremium: boolean) => {
            dispatch(buyBoosters(isPremium));
        },
        closeMultipleBuyDialog: () => {
            dispatch(closeMultipleBuyDialog());
        },
        setPredictProgress: (predictProgress: number) => {
            dispatch(setPredictProgress(predictProgress));
        },
    };
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(MultipleBuyDialog));
